import type { AdminRefundReason, ListAdminRefundsResultDto } from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin refunds surface (AUT-129 / ADMIN-17),
 * targeting:
 *
 *   - `GET  /admin/refunds`        (cursor-paginated, AUT-118 backend)
 *   - `POST /payments/:id/refund`  (the order-initiated issue-refund
 *     ACTION, AUT-105)
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   list → `['admin', 'refunds', 'list', filters]`
 * The refund mutation invalidates by the `['admin', 'refunds']` prefix on
 * success (and the issue-refund hook ALSO invalidates `['admin','orders']`
 * so the order detail's payment tab re-reads `refundedTotal`).
 *
 * `/refunds` is a READ-ONLY history list — no issue button (Igor decision
 * A). The issue-refund action is order-initiated (the order detail's
 * PaymentTab), so the mutation lives here but `RefundsListPage` never
 * imports it.
 *
 * Mirrors `admin-payouts.ts`. The action is role + permission + audit gated
 * server-side (AUT-138 removed per-action TOTP step-up entirely).
 *
 * Server state lives in TanStack; the auth/session permission set lives in
 * MobX. The two never duplicate a datum.
 */

const REFUNDS_BASE = '/admin/refunds';
const PAYMENTS_BASE = '/payments';

/** Stable query-key prefix; the refund mutation invalidates against this. */
export const ADMIN_REFUNDS_KEY = ['admin', 'refunds'] as const;

/**
 * Filter / pagination params for the list query. Mirrors the backend
 * `ListAdminRefundsQuerySchema`:
 *   - `reason` — multi-select over the FULL `ADMIN_REFUND_REASONS` (CSV on
 *     the wire; history can contain any of the six, not just the operator
 *     `admin_manual`);
 *   - `orderId` — exact-match UUID (filtered via the `payment` relation
 *     server-side — `PaymentRefund` has no `orderId` column);
 *   - `dateFrom` / `dateTo` — ISO date strings bounding `createdAt`;
 *   - `cursor` / `limit` — cursor pagination over `createdAt DESC`.
 * NO `q` free-text (not in the query schema).
 */
export interface AdminRefundsListParams {
  reason?: AdminRefundReason[];
  orderId?: string;
  dateFrom?: string;
  dateTo?: string;
  cursor: string | null;
  limit: number;
}

/** Build the URLSearchParams a list fetch sends (omits empty values). */
const buildListParams = (params: AdminRefundsListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.reason && params.reason.length > 0) query.reason = params.reason.join(',');
  if (params.orderId && params.orderId.length > 0) query.orderId = params.orderId;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

export const useAdminRefundsList = (
  params: AdminRefundsListParams,
): UseQueryResult<ListAdminRefundsResultDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_REFUNDS_KEY, 'list', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ListAdminRefundsResultDto>(REFUNDS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

/** Body of the issue-refund POST. `reason` is operator-selectable but
 * defaults to `admin_manual` (the only operator semantics — the other
 * reasons are listener-emitted). `idempotencyKey` is required + caller-
 * supplied (the action is destructive). */
export interface IssueRefundBody {
  amount: string;
  reason: AdminRefundReason;
  recipientId: string;
  idempotencyKey: string;
}

/**
 * Low-level issue-refund mutation against `POST /payments/:id/refund`.
 *
 * Single unconditional POST: AUT-138 removed per-action TOTP step-up, so the
 * refund endpoint never returns `401 totp_step_up_required` again (200 on
 * success, or `409 refund_exceeds_captured` from the independent refund-cap
 * guard). `useIssueRefund` maps the non-success errors to i18n + toast.
 */
export const postIssueRefund = async (paymentId: string, body: IssueRefundBody): Promise<void> => {
  await apiClient.post<unknown>(`${PAYMENTS_BASE}/${paymentId}/refund`, body);
};

/**
 * Cache-helper for the refunds surface. Exposes `invalidate` over the
 * `['admin','refunds']` prefix so the history list refetches after an
 * issue-refund. (The issue-refund hook ALSO invalidates `['admin','orders']`
 * directly so the originating order detail re-reads `refundedTotal`.)
 */
export const useAdminRefundsCache = (): {
  invalidate: () => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_REFUNDS_KEY }),
  };
};
